/**
 * On-panel media cache for signage (TIZEN-9).
 *
 * Foyer streams every asset from the network on every loop. On a Samsung
 * signage panel that is the difference between a wall that plays and one that
 * stalls — and with the WAN down it is the difference between a wall and a
 * black rectangle.
 *
 * THIS IS A PORT OF `nenda-autoplayer/src/lib/localAssetCache.ts`, NOT A
 * REDESIGN. Every constant and predicate below encodes a field bug that repo
 * already paid for, on this hardware, in hotels. They are reproduced with their
 * reasoning attached so nobody "tidies" one back into the bug it prevents. The
 * only deliberate departures are marked FOYER, and each says why.
 *
 * ## The architecture, and why it is split
 *
 * The bundle is served from a remote origin, so `tizen.filesystem` calls made
 * from bundle code are unreliable. The filesystem primitives therefore live in
 * the WGT itself (`tizen/index.html`) and are exposed as exactly two functions:
 *
 *     window.foyerCacheWrite(filename, base64) -> localUri
 *     window.foyerCacheDelete(filename)        -> bool
 *
 * Storage root is `wgt-private`: internal, per-app, and it survives a reboot.
 * Playback feeds the returned URI to the player VERBATIM — only the remote URL
 * is ever `encodeURI`d.
 */

/** One cached asset. `localUri` is what the player is handed. */
interface CacheEntry {
  localUri: string;
  filename: string;
  cachedAt: number;
  bytes: number;
}

type Manifest = Record<string, CacheEntry>;

const MANIFEST_KEY = "foyer.signageAssetCache.v1";

/**
 * Hard caps, so an unattended screen cannot grow the cache without bound now
 * that it survives reboots.
 */
const MAX_CACHE_ENTRIES = 200;
const MAX_EVICTIONS_PER_WRITE = 20;

/**
 * Largest file we will base64-encode and cache.
 *
 * `toBase64()` holds the binary string plus the `btoa()` output alongside the
 * original ArrayBuffer — roughly 2.3x the file in RAM — so a large video can
 * OOM a tight SoC. Oversized assets stream from the remote URL instead.
 *
 * FOYER: the ticket notes this is tunable upward at the Tizen 7 floor, but only
 * against a measurement. It is left at the proven value until somebody has
 * actually watched the memory on a panel; a guess here fails as an OOM on a
 * wall, which is the least debuggable failure this module has.
 */
const MAX_CACHE_FILE_BYTES = 50 * 1024 * 1024;

const dbg = (...args: unknown[]): void => {
  // Into the console, which the ring buffer captures, which `log_capture`
  // carries to the fleet console. A cache that fails silently is how the
  // autoplayer spent weeks writing files that were never served.
  console.log("[signage-cache]", ...args);
};

let manifest: Manifest = {};
let queue: string[] = [];
let processing = false;
let initialized = false;
let evictions = 0;

/**
 * Set when the WGT bridge is missing or private storage cannot be resolved
 * (removed, read-only, permission denied). We then stop caching for the session
 * and always fall back to remote URLs rather than retry-hanging on every asset.
 */
let cachingDisabled = false;
let disabledReason = "";

function loadManifest(): Manifest {
  try {
    return JSON.parse(localStorage.getItem(MANIFEST_KEY) ?? "{}") as Manifest;
  } catch {
    return {};
  }
}

function saveManifest(): void {
  try {
    localStorage.setItem(MANIFEST_KEY, JSON.stringify(manifest));
  } catch (e) {
    dbg("failed to save manifest:", String(e));
  }
}

interface CacheBridge {
  foyerCacheWrite?: (filename: string, base64: string) => Promise<string>;
  foyerCacheDelete?: (filename: string) => Promise<unknown>;
}

function writeFileViaWgt(filename: string, base64: string): Promise<string> {
  const write = (window as unknown as CacheBridge).foyerCacheWrite;
  if (!write) return Promise.reject(new Error("foyerCacheWrite not available"));
  return write(filename, base64);
}

function deleteFileViaWgt(filename: string): Promise<boolean> {
  const del = (window as unknown as CacheBridge).foyerCacheDelete;
  if (!del) return Promise.resolve(false);
  return del(filename).then(
    () => true,
    () => false
  );
}

/**
 * A failure meaning caching is fundamentally unavailable: no bridge, or
 * permission denied. Deliberately BROAD.
 *
 * Tizen reports permission failures under several names — `SecurityError`,
 * `InvalidStateError`, and sometimes a localised "denied" — and almost never
 * the literal word "permission". Matching them all means a genuine denial
 * disables caching for the session instead of retrying, and re-failing, on
 * every asset for the rest of the day.
 *
 * A plain `resolve` failure is NOT included: it can be transient (storage
 * briefly busy) and the WGT re-resolves its handle on the next call, so it must
 * not permanently kill caching.
 */
export function isCachingUnavailableError(e: unknown): boolean {
  const msg = String(e).toLowerCase();
  return (
    msg.includes("not available") ||
    msg.includes("permission") ||
    msg.includes("securityerror") ||
    msg.includes("invalidstateerror") ||
    msg.includes("denied") ||
    msg.includes("not allowed") ||
    msg.includes("not permitted")
  );
}

/**
 * Genuine out-of-space signals only. Deliberately NARROW, and asymmetric with
 * the predicate above.
 *
 * It once matched "unknown" and "io error", which meant a single unrelated
 * write failure evicted the entire cache one entry at a time — the screen then
 * re-downloaded everything, on the loop, forever.
 */
export function isStorageFullError(e: unknown): boolean {
  const msg = String(e).toLowerCase();
  return (
    msg.includes("quota") ||
    msg.includes("no space") ||
    msg.includes("out of space") ||
    msg.includes("storage full") ||
    msg.includes("disk full")
  );
}

function disableCaching(reason: string): void {
  if (cachingDisabled) return;
  cachingDisabled = true;
  disabledReason = reason;
  queue = [];
  dbg("caching disabled for this session:", reason);
}

async function evictOldest(): Promise<boolean> {
  let oldestUrl: string | null = null;
  let oldestTime = Infinity;
  for (const [url, entry] of Object.entries(manifest)) {
    const t = entry.cachedAt ?? 0;
    if (t < oldestTime) {
      oldestTime = t;
      oldestUrl = url;
    }
  }
  if (!oldestUrl) return false;
  await deleteFileViaWgt(manifest[oldestUrl].filename);
  delete manifest[oldestUrl];
  evictions += 1;
  saveManifest();
  return true;
}

/**
 * Write a file, evicting cached entries one at a time until the write succeeds
 * or there is nothing left to evict.
 *
 * A LOOP, not a single retry: one eviction may not free enough space for a
 * larger asset. Capped, so one oversized write cannot wipe the whole cache.
 */
async function writeFileWithEviction(
  filename: string,
  base64: string
): Promise<string> {
  try {
    return await writeFileViaWgt(filename, base64);
  } catch (e) {
    if (!isStorageFullError(e)) throw e;
    let n = 0;
    while (n < MAX_EVICTIONS_PER_WRITE && (await evictOldest())) {
      n += 1;
      dbg("storage full - evicted oldest, retrying");
      try {
        return await writeFileViaWgt(filename, base64);
      } catch (e2) {
        if (!isStorageFullError(e2)) throw e2;
      }
    }
    throw e;
  }
}

/** Stable 32-bit hash of the full URL, base36. */
function hashUrl(url: string): string {
  let h = 0;
  for (let i = 0; i < url.length; i++) {
    h = (Math.imul(31, h) + url.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

/**
 * A collision-proof local filename.
 *
 * Two different URLs routinely share a basename (`.../a/video.mp4` and
 * `.../b/video.mp4`). Keying on the basename alone let one overwrite the other
 * and play the WRONG CONTENT — and was "a likely cause of the cached-playback
 * freezes that led to caching being disabled" in the autoplayer. The hash of
 * the full URL is what makes each remote asset a unique local file.
 */
export function cacheFilenameFor(url: string): string {
  const base = url.split("/").pop()?.split("?")[0] ?? "asset";
  const dot = base.lastIndexOf(".");
  const ext = dot > 0 ? base.slice(dot) : "";
  const stem = (dot > 0 ? base.slice(0, dot) : base).slice(0, 40);
  return `${hashUrl(url)}_${stem}${ext}`;
}

/**
 * Serialises cap enforcement.
 *
 * The after-play path and the background queue can both be mid-await at once.
 * Without a lock their `enforceEntryCap` runs interleave — racing on
 * `evictOldest` (both picking the same "oldest"), each blind to what the other
 * just added — so the cache could quietly exceed its own cap.
 */
let capLock: Promise<void> = Promise.resolve();

async function enforceEntryCap(): Promise<void> {
  const run = capLock.then(async () => {
    while (Object.keys(manifest).length > MAX_CACHE_ENTRIES) {
      if (!(await evictOldest())) break;
    }
  });
  // Keep the chain alive even if a run throws, or the lock wedges rejected.
  capLock = run.catch(() => {});
  return run;
}

function xhrDownload(url: string): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    // Only the REMOTE url is encoded. The local URI the WGT hands back goes to
    // the player verbatim.
    xhr.open("GET", encodeURI(url), true);
    xhr.responseType = "arraybuffer";
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as ArrayBuffer);
      } else {
        reject(new Error("HTTP " + xhr.status));
      }
    };
    xhr.onerror = () => reject(new Error("XHR network error"));
    xhr.send();
  });
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunk))
    );
  }
  return btoa(binary);
}

/** Download, size-check, write, record. Shared by both caching paths. */
async function fetchAndStore(url: string): Promise<void> {
  const filename = cacheFilenameFor(url);
  const buffer = await xhrDownload(url);
  if (buffer.byteLength > MAX_CACHE_FILE_BYTES) {
    dbg(`skipping oversized asset (${buffer.byteLength} bytes): ${filename}`);
    return;
  }
  const localUri = await writeFileWithEviction(filename, toBase64(buffer));
  manifest[url] = {
    localUri,
    filename,
    cachedAt: Date.now(),
    bytes: buffer.byteLength,
  };
  saveManifest();
  await enforceEntryCap();
  dbg("cached:", filename);
}

async function processNext(): Promise<void> {
  if (queue.length === 0) {
    processing = false;
    return;
  }
  const url = queue[0];
  if (manifest[url]) {
    queue.shift();
    setTimeout(processNext, 0);
    return;
  }
  try {
    await fetchAndStore(url);
  } catch (e) {
    dbg("cache failed:", url, String(e));
    if (isCachingUnavailableError(e)) disableCaching(String(e));
  }
  if (cachingDisabled) {
    processing = false;
    return;
  }
  queue.shift();
  setTimeout(processNext, 0);
}

function startDrain(): void {
  if (processing) return;
  processing = true;
  void processNext();
}

/**
 * Load the manifest. Persisting across reboots is the point: a restarted screen
 * plays from disk immediately instead of re-downloading the whole loop.
 *
 * Entries are pruned by `evictStale` (no longer scheduled) and
 * `clearCacheEntry` (the file failed to play), so a stale or bad entry
 * self-heals rather than needing a wipe.
 */
export function initSignageCache(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  manifest = loadManifest();
  dbg(`loaded ${Object.keys(manifest).length} cached entries`);
}

const CACHE_AFTER_PLAY_DELAY_MS = 3000;

/** How many times we defer, waiting for an idle window, before caching anyway. */
const CACHE_AFTER_PLAY_MAX_ATTEMPTS = 5;

/**
 * Cache an asset once it has finished playing.
 *
 * Downloading while a video streams contends for bandwidth and RAM, so we
 * prefer an idle window. But with a back-to-back video playlist the player is
 * effectively always busy, and a one-shot "skip if busy" cached almost nothing
 * — so we defer a BOUNDED number of times and then cache anyway.
 */
export function cacheAfterPlay(remoteUrl: string): void {
  if (cachingDisabled || !remoteUrl) return;
  if (manifest[remoteUrl]) return;
  scheduleAfterPlay(remoteUrl, 0);
}

/**
 * FOYER: the autoplayer asks `webapis.avplay.getState()`. Foyer's signage
 * surface plays through a plain `<video>`, so "busy" is asked of the DOM
 * instead - any video element that is playing and not ended.
 */
function playerIsBusy(): boolean {
  try {
    const vids = document.querySelectorAll("video");
    for (const v of Array.from(vids)) {
      if (!v.paused && !v.ended && v.readyState > 2) return true;
    }
    return false;
  } catch {
    return false;
  }
}

function scheduleAfterPlay(remoteUrl: string, attempt: number): void {
  setTimeout(() => {
    if (cachingDisabled) return;
    if (manifest[remoteUrl]) return;
    if (playerIsBusy() && attempt < CACHE_AFTER_PLAY_MAX_ATTEMPTS) {
      scheduleAfterPlay(remoteUrl, attempt + 1);
      return;
    }
    fetchAndStore(remoteUrl).catch((e) => {
      dbg("cache failed:", remoteUrl, String(e));
      if (isCachingUnavailableError(e)) disableCaching(String(e));
    });
  }, CACHE_AFTER_PLAY_DELAY_MS);
}

/**
 * Queue a whole playlist for background download.
 *
 * FOYER: NOT version-gated. The autoplayer disables this below Tizen 7 because
 * that SoC cannot download while AVPlay decodes; the Tizen 7 floor (TIZEN-1)
 * removes the reason. Playback smoothness while the queue fills still wants
 * confirming on hardware.
 */
export function queueSignageAssets(urls: readonly string[]): void {
  if (cachingDisabled) return;
  const fresh = urls.filter(
    (url) => url && !manifest[url] && !queue.includes(url)
  );
  if (!fresh.length) return;
  queue.push(...fresh);
  startDrain();
}

/** The local URI for a remote asset, or null to stream it. */
export function getLocalUri(remoteUrl: string): string | null {
  if (cachingDisabled) return null;
  return manifest[remoteUrl]?.localUri ?? null;
}

/**
 * Drop an entry whose file would not play.
 *
 * The caller falls back to the remote URL and does NOT report a playback
 * failure: several dead entries in a row would otherwise trip the playback
 * circuit breaker and blank the wall, turning a cache miss into an outage.
 */
export function clearCacheEntry(remoteUrl: string): void {
  const entry = manifest[remoteUrl];
  if (!entry) return;
  delete manifest[remoteUrl];
  saveManifest();
  void deleteFileViaWgt(entry.filename);
  dbg("cleared bad cache entry:", entry.filename);
}

/**
 * Drop everything no longer scheduled.
 *
 * THE EMPTY GUARD IS LOAD-BEARING: a momentarily empty playlist - a config
 * refresh in flight, a schedule between windows - once made this delete the
 * entire cache, and the wall then re-downloaded everything it had just thrown
 * away. An empty active set means "we do not know right now", never "nothing is
 * scheduled".
 */
export function evictStale(activeUrls: readonly string[]): void {
  if (activeUrls.length === 0) return;
  const active = new Set(activeUrls);
  let n = 0;
  for (const url of Object.keys(manifest)) {
    if (active.has(url)) continue;
    // Delete the FILE, not just the row - dropping only the manifest entry
    // orphans the file forever and slowly fills the disk.
    void deleteFileViaWgt(manifest[url].filename);
    delete manifest[url];
    n += 1;
  }
  if (n > 0) {
    evictions += n;
    saveManifest();
    dbg(`evicted ${n} assets no longer scheduled`);
  }
}

/**
 * Cache state for the heartbeat.
 *
 * Reported because a silent kill switch is how the autoplayer spent weeks
 * writing files that were never served. `disabled` names its reason, so the
 * fleet console can tell "this panel has no cache" from "this panel's cache is
 * empty".
 */
export function signageCacheReport(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const entries = Object.keys(manifest).length;
  if (!initialized && !entries) return undefined;
  const bytes = Object.values(manifest).reduce(
    (sum, e) => sum + (e.bytes || 0),
    0
  );
  const parts = [
    `entries=${entries}`,
    `mb=${(bytes / 1048576).toFixed(1)}`,
    `queued=${queue.length}`,
    `evicted=${evictions}`,
  ];
  if (cachingDisabled) parts.push(`disabled=${disabledReason.slice(0, 80)}`);
  return parts.join(" ");
}

/** Test seam: forget everything this module is holding in memory. */
export function resetSignageCacheForTest(): void {
  manifest = {};
  queue = [];
  processing = false;
  initialized = false;
  cachingDisabled = false;
  disabledReason = "";
  evictions = 0;
  capLock = Promise.resolve();
  try {
    localStorage.removeItem(MANIFEST_KEY);
  } catch {
    /* nothing to do */
  }
}
